import { sample } from 'lodash-es'
import { treq } from '#app/lib/treq'
import type { AbsoluteUrl, ColorHexCode, Url } from '#server/types/common'
import type { CouchUuid } from '#server/types/couchdb'
import type { ImageDataUrl } from '#server/types/image'

export const imageMaxSize = 1600

export async function getUrlDataUrl (url: AbsoluteUrl) {
  const { 'data-url': dataUrl } = await treq('images', 'get', 'data-url', { url })
  return dataUrl as ImageDataUrl
}

export async function getUserGravatarUrl () {
  const { url } = await treq('images', 'get', 'gravatar')
  return url
}

interface ImageDimensions {
  width?: number
  height?: number
}
interface ImageData {
  original: ImageDimensions
  resized: ImageDimensions
  dataUrl?: string
}

export function resizeDataUrl (dataURL: ImageDataUrl, maxSize: number): Promise<ImageData> {
  return new Promise((resolve, reject) => {
    const data: ImageData = { original: {}, resized: {} }
    const image = new Image()
    image.onload = () => {
      const canvas = document.createElement('canvas')
      let { width, height } = image
      data.original = { width, height }
      ;[ width, height ] = getResizedDimensions(width, height, maxSize)
      data.resized = { width, height }

      canvas.width = width
      canvas.height = height
      canvas.getContext('2d').drawImage(image, 0, 0, width, height)
      data.dataUrl = canvas.toDataURL('image/png')
      return resolve(data)
    }

    image.onerror = () => reject(new Error('invalid image'))

    image.src = dataURL
  })
}

export function getNonResizedUrl (url: Url) {
  return url.replace(/\/img\/users\/\d+x\d+\//, '/img/')
}

export function getColorSquareDataUri (colorHexCode: string) {
  colorHexCode = normalizeColorHexCode(colorHexCode)
  // Using the base64 version and not the utf8, as it gets problematic
  // when used as background-image '<div style="background-image: url({{imgSrc picture 100}})"'
  const base64Hash = btoa(`<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 1 1'><path d='M0,0h1v1H0' fill='${colorHexCode}'/></svg>`)
  return `data:image/svg+xml;base64,${base64Hash}` as ImageDataUrl
}

const normalizeColorHexCode = code => (code[0] === '#' ? code : `#${code}`) as ColorHexCode

export function getColorSquareDataUriFromCouchUuId (couchUuid: CouchUuid) {
  const colorHexCode = getColorHexCodeFromCouchUuId(couchUuid)
  return getColorSquareDataUri(colorHexCode)
}

function getResizedDimensions (width, height, maxSize) {
  if (width > height) {
    if (width > maxSize) {
      height *= maxSize / width
      width = maxSize
    }
  } else {
    if (height > maxSize) {
      width *= maxSize / height
      height = maxSize
    }
  }
  return [ width, height ]
}

// Inspired by https://www.materialpalette.com/colors
const someSuggestedColors = [
  '009688',
  '00bcd4',
  '03a9f4',
  '2196f3',
  '3f51b5',
  '4caf50',
  '607d8b',
  '673ab7',
  '795548',
  '8bc34a',
  '9c27b0',
  '9e9e9e',
  'cddc39',
]

export function getColorHexCodeFromCouchUuId (couchUuid: string) {
  if (couchUuid == null) return someSuggestedColors[0]
  const someStableDocNumber = parseInt(couchUuid.slice(-2), 16)
  // Pick one of the colors based on the group slug length
  const index = someStableDocNumber % someSuggestedColors.length
  return someSuggestedColors[index]
}

export const getSomeColorHexCodeSuggestion = () => `#${sample(someSuggestedColors)}` as ColorHexCode
